import {
  FetchAllStocksParams,
  FetchAllStocksAPIResponse,
  MarketInfoResponse,
  FetchStocksCategoriesApiResponse,
  StockDetailsApiResponse,
  StockDetailsParams,
  StockHistoricalPricesParams,
  StockHistoricalPricesApiResponse,
  GetStocksCountApiResponse,
  GetSharePricePerUnitParams,
  GetSharePricePerUnitApiResponse,
  GetShareQuotesParams,
  GetShareQuotesApiResponse,
  GetPortfolioPositionParams,
  PortfolioInfoApiResponse,
  PortfolioPositionApiResponse,
  OrderPayload,
  SellOrderPayload,
  OrderResponse,
  PaginatedStocksResponse,
} from './sharesTypes'

import { api } from '@/api/api'
import { endpoints } from '@/api/endpoints'

function serializeParams(params: Record<string, unknown>): string {
  const parts: string[] = []
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      for (const item of value) {
        parts.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`,
        )
      }
    } else {
      parts.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
      )
    }
  }
  return parts.join('&')
}

export async function fetchMarketInfo(): Promise<MarketInfoResponse> {
  const response = await api.get<MarketInfoResponse>(
    endpoints.shares.getMarketInfo,
  )
  return response.data
}

export async function fetchStocks(
  params: FetchAllStocksParams,
): Promise<FetchAllStocksAPIResponse> {
  const response = await api.get<FetchAllStocksAPIResponse>(
    endpoints.shares.fetchAllStocks,
    {
      params,
      paramsSerializer: (p) => serializeParams(p as Record<string, unknown>),
    },
  )
  return response.data
}

export async function fetchMyStocks(
  params: FetchAllStocksParams,
): Promise<PaginatedStocksResponse> {
  const response = await api.get<PaginatedStocksResponse>(
    endpoints.shares.fetchMyStocks,
    {
      params,
      paramsSerializer: (p) => serializeParams(p as Record<string, unknown>),
    },
  )
  return response.data
}

export async function fetchStockCategories(): Promise<FetchStocksCategoriesApiResponse> {
  const response = await api.get<FetchStocksCategoriesApiResponse>(
    endpoints.shares.fetchStocksCategories,
  )
  return response.data
}

export async function fetchStockDetails(
  params: StockDetailsParams,
): Promise<StockDetailsApiResponse> {
  const response = await api.get<StockDetailsApiResponse>(
    endpoints.shares.getStockDetails,
    { params },
  )
  return response.data
}

export async function fetchStockHistoricalPrices(
  params: StockHistoricalPricesParams,
): Promise<StockHistoricalPricesApiResponse> {
  const response = await api.get<StockHistoricalPricesApiResponse>(
    endpoints.shares.getStockHistoricalPrices,
    { params },
  )
  return response.data
}

export async function getStocksCount(): Promise<GetStocksCountApiResponse> {
  const response = await api.get<GetStocksCountApiResponse>(
    endpoints.shares.getStocksCount,
  )
  return response.data
}

export async function getPortfolioInfo(): Promise<PortfolioInfoApiResponse> {
  const response = await api.get<PortfolioInfoApiResponse>(
    endpoints.shares.getPortfolioInfo,
  )
  return response.data
}

export async function getPortfolioPosition(
  params: GetPortfolioPositionParams,
): Promise<PortfolioPositionApiResponse> {
  const response = await api.get<PortfolioPositionApiResponse>(
    endpoints.shares.getPortfolioPosition,
    { params },
  )
  return response.data
}

export async function getSharePricePerUnit(
  params: GetSharePricePerUnitParams,
): Promise<GetSharePricePerUnitApiResponse> {
  const response = await api.get<GetSharePricePerUnitApiResponse>(
    endpoints.shares.getPricePerUnit,
    { params },
  )
  return response.data
}

export async function getShareQuotes(
  params: GetShareQuotesParams,
): Promise<GetShareQuotesApiResponse> {
  const response = await api.post<GetShareQuotesApiResponse>(
    endpoints.shares.getShareQuotes,
    params,
  )
  return response.data
}

export async function createBuyOrder(
  payload: OrderPayload,
): Promise<OrderResponse> {
  const response = await api.post<OrderResponse>(
    endpoints.shares.limitBuy,
    payload,
  )
  return response.data
}

export async function createSellOrder(
  payload: SellOrderPayload,
): Promise<OrderResponse> {
  const response = await api.post<OrderResponse>(
    endpoints.shares.limitSell,
    payload,
  )
  return response.data
}

export const sharesApi = {
  fetchStocks,
  fetchMyStocks,
  fetchStockCategories,
  fetchMarketInfo,
  fetchStockDetails,
  fetchStockHistoricalPrices,
  getStocksCount,
  getPortfolioInfo,
  getPortfolioPosition,
  getSharePricePerUnit,
  getShareQuotes,
  createBuyOrder,
  createSellOrder,
}
